import { useEffect, useRef } from 'react';
import { usePWA } from './usePWA';
import { toast } from 'sonner';

export const usePWANotifications = () => {
  const { isInstalled, updateAvailable } = usePWA();
  const prevOnlineRef = useRef(navigator.onLine);
  const offlineTimestampRef = useRef<number | null>(null);
  const hasShownUpdateToastRef = useRef(false);
  const hasShownInstalledToastRef = useRef(false);

  // Track online/offline status changes
  useEffect(() => {
    const handleOnline = () => {
      const offlineTimestamp = offlineTimestampRef.current;
      const now = Date.now();
      
      // Only show "back online" toast if offline for more than 5 seconds
      if (offlineTimestamp && (now - offlineTimestamp) > 5000) {
        toast.success('Back online!', {
          duration: 3000,
        });
      }
      
      prevOnlineRef.current = true;
      offlineTimestampRef.current = null;
    };

    const handleOffline = () => {
      offlineTimestampRef.current = Date.now();
      
      toast.warning("You're offline - game still works!", {
        duration: 5000,
      });
      
      prevOnlineRef.current = false;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Show update available notification
  useEffect(() => {
    if (updateAvailable && !hasShownUpdateToastRef.current) {
      hasShownUpdateToastRef.current = true;
      
      toast.info('Update available!', {
        description: 'Refresh to get the latest version',
        duration: Infinity, // Persistent toast
        action: {
          label: 'Refresh Now',
          onClick: () => window.location.reload(),
        },
      });
    }
  }, [updateAvailable]);

  // Show app installed notification
  useEffect(() => {
    const handleAppInstalled = () => {
      if (!hasShownInstalledToastRef.current) {
        hasShownInstalledToastRef.current = true;
        
        toast.success('App installed successfully!', {
          duration: 4000,
        });
      }
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  return null;
};
